import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBulkRestoreAssetsMutation,
  useRemoveAssetFromModuleMutation
} from '@ports/internal-services/trash/trashSlice';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';

interface BulkOperationsParams {
  selectedPhotos?: string[];
  selectedPhotoFolders?: string[];
  selectedRecipes?: string[];
  selectedRecipeFolders?: string[];
  selectedMoments?: string[];
  selectedMemories?: string[];
  selectedPeople?: string[];
  onClearSelections?: () => void;
  onRefetchData?: () => void;
}

export const useTrashBulkOperations = ({
  selectedPhotos = [],
  selectedPhotoFolders = [],
  selectedRecipes = [],
  selectedRecipeFolders = [],
  selectedMoments = [],
  selectedMemories = [],
  selectedPeople = [],
  onClearSelections,
  onRefetchData
}: BulkOperationsParams = {}) => {
  const { t } = useTranslation();
  const [isBulkRestoreConfirm, setIsBulkRestoreConfirm] = useState(false);
  const [isBulkDeleteConfirm, setIsBulkDeleteConfirm] = useState(false);

  const [RestoreModule] = useBulkRestoreAssetsMutation();
  const [RemoveAssetFromModule] = useRemoveAssetFromModuleMutation();

  const totalSelectedCount =
    selectedPhotos.length +
    selectedRecipes.length +
    selectedMoments.length +
    selectedMemories.length +
    selectedPhotoFolders.length +
    selectedRecipeFolders.length +
    selectedPeople.length;

  const handleBulkRestore = () => {
    setIsBulkRestoreConfirm(true);
  };

  const confirmBulkRestore = async () => {
    try {
      const restorePromises = [];

      // Restore assets (photos)
      if (selectedPhotos.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'assets',
              moduleId: selectedPhotos
            }
          }).unwrap()
        );
      }

      // Restore photo folders
      if (selectedPhotoFolders.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'assets',
              folderIds: selectedPhotoFolders
            }
          }).unwrap()
        );
      }

      // Restore recipes
      if (selectedRecipes.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'recipes',
              moduleId: selectedRecipes
            }
          }).unwrap()
        );
      }

      // Restore recipe folders
      if (selectedRecipeFolders.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'recipes',
              folderIds: selectedRecipeFolders
            }
          }).unwrap()
        );
      }

      // Restore moments
      if (selectedMoments.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'moments',
              moduleId: selectedMoments
            }
          }).unwrap()
        );
      }

      // Restore memories
      if (selectedMemories.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'memories',
              moduleId: selectedMemories
            }
          }).unwrap()
        );
      }

      // Restore people
      if (selectedPeople.length > 0) {
        restorePromises.push(
          RestoreModule({
            bulkRestoreTrash: {
              module: 'people',
              moduleId: selectedPeople
            }
          }).unwrap()
        );
      }

      // Wait for all restore operations
      await Promise.all(restorePromises);

      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('bulkRestoreSuccess')
        }
      });

      setIsBulkRestoreConfirm(false);

      // Clear selections and refetch
      onClearSelections?.();
      onRefetchData?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('bulkRestoreError')
        }
      });
      setIsBulkRestoreConfirm(false);
    }
  };

  const handleBulkDelete = () => {
    setIsBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const modules = [];

      // Prepare assets module (photos and photo folders)
      if (selectedPhotos.length > 0 || selectedPhotoFolders.length > 0) {
        modules.push({
          module: 'assets',
          moduleIds: selectedPhotos,
          folderIds: selectedPhotoFolders
        });
      }

      // Prepare recipes module (recipes and recipe folders)
      if (selectedRecipes.length > 0 || selectedRecipeFolders.length > 0) {
        modules.push({
          module: 'recipes',
          moduleIds: selectedRecipes,
          folderIds: selectedRecipeFolders
        });
      }

      // Prepare moments module
      if (selectedMoments.length > 0) {
        modules.push({
          module: 'moments',
          moduleIds: selectedMoments,
          folderIds: []
        });
      }

      // Prepare memories module
      if (selectedMemories.length > 0) {
        modules.push({
          module: 'memories',
          moduleIds: selectedMemories,
          folderIds: []
        });
      }

      // Prepare people module
      if (selectedPeople.length > 0) {
        modules.push({
          module: 'people',
          moduleIds: selectedPeople,
          folderIds: []
        });
      }

      // Call API with all modules at once
      if (modules.length > 0) {
        await RemoveAssetFromModule({
          createTrashModule: {
            modules: modules
          }
        }).unwrap();

        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: t('bulkDeleteSuccess')
          }
        });

        setIsBulkDeleteConfirm(false);

        // Clear selections and refetch
        onClearSelections?.();
        onRefetchData?.();
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('bulkDeleteError')
        }
      });
      setIsBulkDeleteConfirm(false);
    }
  };

  return {
    totalSelectedCount,
    isBulkRestoreConfirm,
    setIsBulkRestoreConfirm,
    isBulkDeleteConfirm,
    setIsBulkDeleteConfirm,
    handleBulkRestore,
    confirmBulkRestore,
    handleBulkDelete,
    confirmBulkDelete
  };
};
